import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { COLLECTION_UPLOAD_KIND, THUMBNAIL_MAX_EDGE, UPLOAD_LIMITS, formatBytes, newId } from '@app/shared';
import { api, ApiError, OfflineError } from '../api.ts';
import { db } from '../db.ts';

/**
 * File handling on the device: thumbnails are made here (canvas), files wait
 * in IndexedDB until their record exists on the server, then upload. Until
 * then the UI shows the local copy, so adding photos works offline.
 */

export interface PreparedFile {
  fileId: string;
  name: string;
  type: string;
  size: number;
  width: number | null;
  height: number | null;
  thumb: Blob | null;
  blob: Blob;
}

export function validateForCollection(file: File, collection: string): string | null {
  const kind = COLLECTION_UPLOAD_KIND[collection];
  if (!kind) return 'Files are not supported here.';
  const limits = UPLOAD_LIMITS[kind];
  if (file.size > limits.maxBytes) return `"${file.name}" is larger than ${formatBytes(limits.maxBytes)}.`;
  // Browsers sometimes leave type empty (e.g. HEIC on Windows); the server checks the real bytes.
  if (file.type && !limits.types.includes(file.type)) return `"${file.name}" isn't a supported ${kind === 'image' ? 'photo' : 'file'} type.`;
  return null;
}

async function makeThumbnail(file: Blob): Promise<{ thumb: Blob | null; width: number | null; height: number | null }> {
  if (typeof createImageBitmap === 'undefined' || typeof document === 'undefined') return { thumb: null, width: null, height: null };
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, THUMBNAIL_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const thumb = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    const result = { thumb, width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return result;
  } catch {
    // Formats the browser can't decode (often HEIC) upload without a preview.
    return { thumb: null, width: null, height: null };
  }
}

export async function prepareFile(file: File, withThumbnail: boolean): Promise<PreparedFile> {
  const meta = withThumbnail && file.type.startsWith('image/') ? await makeThumbnail(file) : { thumb: null, width: null, height: null };
  return { fileId: newId(), name: file.name || 'file', type: file.type || 'application/octet-stream', size: file.size, blob: file, ...meta };
}

export async function queueUpload(p: PreparedFile, familyId: string, collection: string, recordId: string): Promise<void> {
  await db.uploads.put({
    fileId: p.fileId,
    familyId,
    collection,
    recordId,
    name: p.name,
    type: p.type,
    blob: p.blob,
    thumb: p.thumb,
    createdAt: new Date().toISOString(),
    state: 'queued',
  });
}

/** Upload queued files whose records have reached the server. Called by the sync engine. */
export async function processUploads(): Promise<void> {
  const queued = (await db.uploads.toArray()).filter((u) => u.state === 'queued');
  for (const u of queued) {
    const record = await db.records(u.collection).get(u.recordId);
    if (!record) {
      await db.uploads.delete(u.fileId);
      continue;
    }
    // The server only accepts a file once the record that references it is synced.
    const pendingChange = (await db.outbox.where('[collection+recordId]').equals([u.collection, u.recordId]).count()) > 0;
    if (record.rev === 0 || pendingChange) continue;
    const form = new FormData();
    form.append('id', u.fileId);
    form.append('refCollection', u.collection);
    form.append('refId', u.recordId);
    form.append('file', u.blob, u.name);
    if (u.thumb) form.append('thumb', u.thumb, 'thumb.jpg');
    try {
      await api('/api/files', { method: 'POST', body: form });
      await db.uploads.delete(u.fileId);
    } catch (err) {
      if (err instanceof OfflineError) throw err;
      if (err instanceof ApiError && err.status !== 401 && err.status < 500) {
        await db.uploads.update(u.fileId, { state: 'failed', error: err.message });
      }
    }
  }
}

/**
 * URL for displaying a file: the local copy while it waits to upload,
 * otherwise the server URL (the service worker caches thumbnails for offline).
 */
export function useFileSrc(fileId: string | null | undefined, variant: 'thumb' | 'original' = 'thumb'): string | undefined {
  const pending = useLiveQuery(async () => (fileId ? ((await db.uploads.get(fileId)) ?? null) : null), [fileId]);
  const [objectUrl, setObjectUrl] = useState<string | undefined>();
  useEffect(() => {
    if (!pending) return setObjectUrl(undefined);
    const blob = variant === 'thumb' ? (pending.thumb ?? pending.blob) : pending.blob;
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pending, variant]);
  if (!fileId) return undefined;
  if (pending === undefined) return undefined;
  if (pending) return objectUrl;
  return variant === 'thumb' ? `/api/files/${fileId}?variant=thumb` : `/api/files/${fileId}`;
}

export function fileUrl(fileId: string, download = false): string {
  return `/api/files/${fileId}${download ? '?download=1' : ''}`;
}

const PINNED = 'file-pinned';

/** Cache Storage only exists on HTTPS (or localhost); plain-http LAN access can't keep files offline. */
export const canPinFiles = () => typeof caches !== 'undefined';

/** Keep a file's original available offline (e.g. an insurance card for emergencies). */
export async function pinFile(fileId: string): Promise<void> {
  if (!canPinFiles()) throw new Error('Keeping files offline needs a secure (HTTPS) connection.');
  const cache = await caches.open(PINNED);
  await cache.add(`/api/files/${fileId}`);
}

export async function unpinFile(fileId: string): Promise<void> {
  if (typeof caches === 'undefined') return;
  await (await caches.open(PINNED)).delete(`/api/files/${fileId}`);
}

export async function isFilePinned(fileId: string): Promise<boolean> {
  if (typeof caches === 'undefined') return false;
  return Boolean(await (await caches.open(PINNED)).match(`/api/files/${fileId}`));
}
